import * as React from "react";
import { Box, Chip, ChipDelete, Input, Stack, Typography } from "@mui/joy";
import IconButton from "@mui/joy/IconButton";
import AddIcon from "@mui/icons-material/Add";
import { Field } from "./types";
import { Clear } from "@mui/icons-material";

interface MultiEmailInputProps {
  field: Field;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

export default function MultiEmailInput({ field, value, onChange, disabled }: MultiEmailInputProps) {
  const [currentEmail, setCurrentEmail] = React.useState("");
  const [emailError, setEmailError] = React.useState(false);

  const emails = value ? value.split(",").filter((email) => email.trim()) : [];

  const validateEmail = (email: string): boolean => {
    if (!field.validation?.pattern) return true;
    return RegExp(field.validation.pattern).test(email);
  };

  const addEmail = () => {
    const email = currentEmail.trim();
    if (!email) return;

    if (!validateEmail(email)) {
      setEmailError(true);
      return;
    }

    if (emails.includes(email)) return;

    const newEmails = [...emails, email];
    onChange(newEmails.join(","));
    setCurrentEmail("");
    setEmailError(false);
  };

  const removeEmail = (emailToRemove: string) => {
    const newEmails = emails.filter((email) => email !== emailToRemove);
    onChange(newEmails.join(","));
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      addEmail();
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentEmail(event.target.value);
    setEmailError(false);
  };

  return (
    <Stack spacing={1}>
      {emails.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {emails.map((email, index) => (
            <Chip
              key={index}
              variant="soft"
              color="primary"
              endDecorator={
                <ChipDelete 
                  color="danger" 
                  variant="plain" 
                  onClick={() => removeEmail(email)}
                  sx={{ 
                    minWidth: 24, 
                    minHeight: 24,
                    '& svg': { fontSize: '1.2rem' },
                    '@media (pointer: coarse)': {
                      minWidth: 32,
                      minHeight: 32,
                      '& svg': { fontSize: '1.4rem' }
                    }
                  }}
                >
                  <Clear />
                </ChipDelete>
              }
              disabled={disabled}
            >
              {email}
            </Chip>
          ))}
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
        <Input
          value={currentEmail}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          placeholder={field.example || "Enter email address"}
          error={emailError}
          disabled={disabled}
          sx={{ flexGrow: 1 }}
        />
        <IconButton onClick={addEmail} disabled={disabled || !currentEmail.trim()} color="primary" variant="soft">
          <AddIcon />
        </IconButton>
      </Box>

      {emailError && (
        <Typography level="body-xs" color="danger">
          Please enter a valid email address
        </Typography>
      )}
    </Stack>
  );
}
